import { Button, Card, CardContent } from "@roberttamas/ui";
import { format } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Check, Copy } from "@/components/icons";
import { StepFooter, useStandaloneStep } from "@/components/setup/step-chrome";
import { STEP_BY_ID, type SetupStepProps } from "@/components/setup/steps";
import { currentDateFnsLocale } from "@/lib/locale-format";
import { buildUploaderUrl, uploaderDisplayPath } from "@/lib/uploader-url";

/**
 * Final wizard screen — the shareable link + QR for guests, and the
 * reassurance that everything stays editable from My orders.
 */
export function StepDone({ setup, linear, onDone, onBack }: SetupStepProps) {
  const { t } = useTranslation();
  const standalone = useStandaloneStep();
  const { site } = setup;
  if (!site) return null;

  const url = buildUploaderUrl(site.slug);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("setup.done.linkCopied"));
    } catch {
      toast.error(t("setup.done.copyFailed"));
    }
  };

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        {!standalone && (
          <div className="space-y-1 text-center">
            <Check className="mx-auto h-10 w-10 rounded-full bg-green-600/10 p-2 text-green-600" />
            <h1 className="text-2xl font-semibold">
              {t(STEP_BY_ID.done.labelKey)}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(STEP_BY_ID.done.subtitleKey)}
            </p>
          </div>
        )}

        <div className="flex flex-col items-center gap-4">
          <div className="rounded-lg border bg-white p-4">
            <QRCodeSVG value={url} size={180} level="M" />
          </div>
          <div className="flex w-full max-w-md items-center justify-between gap-2 rounded-md border px-3 py-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="truncate font-mono text-sm underline underline-offset-4"
            >
              {uploaderDisplayPath(site.slug)}
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={t("setup.done.copyLink")}
              onClick={() => void copy()}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {site.expiresAt && (
          <p className="text-center text-sm text-muted-foreground">
            <Trans
              i18nKey="setup.done.uploadsUntil"
              values={{
                date: format(new Date(site.expiresAt), "PPP", { locale: currentDateFnsLocale() }),
              }}
              components={{ b: <strong /> }}
            />
          </p>
        )}

        {/* Standalone: no primary action at all — the PageHeader's Back
            link already covers "Back to overview". */}
        {!standalone && (
          <StepFooter onBack={onBack}>
            <Button size="lg" onClick={onDone}>
              {linear ? t("setup.done.doneCta") : t("setup.done.backToOverview")}
            </Button>
          </StepFooter>
        )}
      </CardContent>
    </Card>
  );
}
